/*
 * sts-bloggle-enhance.js
 *
 * Restores the two inline-shortcode features that a Bloggle app update deleted
 * from sections/main-bloggle-article.liquid on 2026-08-18 (commit af44ef0e):
 *
 *   [[T13,T18]] / [[ABC-1234]]  ->  in-article product slider
 *   [[ctaquiz]]                 ->  state-aware quiz call-to-action button
 *
 * Why this lives here and not in the section that renders the article: the
 * Bloggle app rewrites sections/main-bloggle-article.liquid wholesale whenever
 * it updates, which is exactly how the previous copy of this logic was lost.
 * Nothing of ours can survive in that file, so the logic sits in this asset and
 * is wired in from sections/sts-bloggle-enhance.liquid, neither of which the
 * app owns.
 *
 * The standard article template does the same job in Liquid: sections/
 * sts-article.liquid rewrites `<p>[[...]]</p>` into a slider root before the
 * body is printed. Bloggle prints `{{ article.content }}` itself, so the same
 * rewrite has to happen against the DOM here. The markup produced is identical,
 * which keeps assets/article.js the single renderer for both templates.
 *
 * [[ctaquiz]] is the one place the two templates differ in HOW, never in what.
 * The standard template substitutes snippets/sts-article-cta.liquid into the
 * body server-side; here the same snippet is server-rendered once into the
 * <template data-sts-ctaquiz> that snippets/sts-shortcode-config.liquid emits,
 * and article.js clones it into each root. Same markup, same copy, same routes,
 * and the <head> rules from snippets/sts-head-state.liquid pick the state before
 * paint on both. This is what replaced the grey "Take the Quiz" button that
 * ignored whether the reader had already taken it (burn-down S5).
 */
(function () {
  'use strict';

  /* Bloggle wraps editor blocks in .bggle--block.bggle_text and plain rich-text
     bodies in .bloggle_rte. .bggle--slider is the app's own product slider and
     is left alone. */
  var BLOCK_SELECTOR = '.bggle--block.bggle_text, .bloggle_rte';
  var APP_SLIDER_SELECTOR = '.bggle--slider';

  var SHORTCODE = /\[\[([^[\]]+)\]\]/;

  /* The tag / SKU lists article.js understands: "T13", "T13,T18", "ABC-1234",
     a trailing item count ("T13,T18,6") and an optional "| show_message=true"
     suffix. Anything else is a typo or prose in double brackets, and is left on
     the page exactly as the author typed it. */
  var COMMAND = /^[A-Za-z0-9][\w .+-]*(?:\s*,\s*[A-Za-z0-9][\w .+-]*)*(?:\s*\|\s*[\w =]+)?$/;

  /* Builds the same root the standard article template builds in Liquid. No
     .productCard--loader skeletons inside it: the standard template prints four
     because its roots are in the HTML from the first byte, whereas these are
     built after the body has already painted, so a skeleton would appear and
     vanish in the same frame. The root reserves its height either way. */
  function rootFor(rawCommand) {
    var command = (rawCommand || '').trim();
    if (!command) {
      return null;
    }

    if (command.toLowerCase() === 'ctaquiz') {
      command = 'ctaquiz';
    } else if (!COMMAND.test(command)) {
      return null;
    }

    var root = document.createElement('div');
    root.className = 'article__product-slider--root inline-product-slider';
    root.setAttribute('data-tags', command);
    return root;
  }

  function process() {
    var created = 0;
    var blocks = document.querySelectorAll(BLOCK_SELECTOR);

    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.closest && block.closest(APP_SLIDER_SELECTOR)) {
        continue;
      }

      var paragraphs = block.querySelectorAll('p');
      for (var j = 0; j < paragraphs.length; j++) {
        var paragraph = paragraphs[j];
        if (paragraph.hasAttribute('data-processed') || !paragraph.parentNode) {
          continue;
        }

        /* .bloggle_rte is the whole article body, so it also matches paragraphs
           sitting inside one of the app's own slider blocks. Never touch those. */
        if (paragraph.closest && paragraph.closest(APP_SLIDER_SELECTOR)) {
          continue;
        }

        var text = paragraph.textContent;
        if (!text || text.indexOf('[[') === -1) {
          continue;
        }

        // An unclosed "[[" or an unusable command leaves the paragraph alone.
        var match = text.match(SHORTCODE);
        if (!match) {
          continue;
        }

        var root = rootFor(match[1]);
        if (!root) {
          continue;
        }

        paragraph.setAttribute('data-processed', 'true');
        paragraph.parentNode.replaceChild(root, paragraph);
        created++;
      }
    }

    return created;
  }

  /* article.js renders each root once and is safe to call again, so a late
     Bloggle block only costs the roots it actually added. */
  function render() {
    if (typeof window.cacheElements !== 'function' || typeof window.renderSliders !== 'function') {
      console.error('[sts-bloggle-enhance] article.js is not loaded; shortcodes will not render');
      return;
    }

    try {
      window.cacheElements();
      var rendering = window.renderSliders();
      if (rendering && typeof rendering.catch === 'function') {
        rendering.catch(function (error) {
          console.error('[sts-bloggle-enhance] renderSliders failed', error);
        });
      }
    } catch (error) {
      console.error('[sts-bloggle-enhance] renderSliders failed', error);
    }
  }

  function run() {
    var created;
    try {
      created = process();
    } catch (error) {
      console.error('[sts-bloggle-enhance] shortcode processing failed', error);
      return;
    }

    if (created > 0) {
      render();
    }
  }

  // This script is deferred, so the article body is already parsed.
  run();

  /* Bloggle can swap blocks in after load. Watch the article body briefly so a
     late block still gets its shortcodes, then stop - bounded on purpose, so a
     long-lived tab is not left with a live observer. */
  if (window.MutationObserver) {
    var container = document.getElementById('bloggle_rte') || document.body;
    var pending = null;
    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(run, 150);
    });

    observer.observe(container, { childList: true, subtree: true });

    setTimeout(function () {
      observer.disconnect();
      clearTimeout(pending);
    }, 5000);
  }
})();
